import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout, { ViewPanelLayout } from 'Layout';
import SettingsHeader from 'components/SettingsHeader';
import Grid from '@mui/material/Grid';
import InputTextFiled from '@design-system/TextField';
import ButtonPrimary from '@design-system/ButtonPrimary';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import Skeleton from '@mui/material/Skeleton';

import useNotification from '@design-system/Notification/useNotification';

import { useSettingsCashRegister } from 'containers/SettingsCashRegister/hooks';

import { errorsCodeTypes } from 'utils/parrotErrorCode';

import { useGetCashRegister } from './hooks';
import { updateCashRegister } from './services';

function SettingsCashRegister() {
  const { t } = useTranslation();
  const history = useHistory();
  const { setNotification } = useNotification();
  const { data: cashRegister, isLoading, isSuccess, refetch: refetchCashRegister } = useGetCashRegister();
  const { refetch: refetchSettingsCashRegister } = useSettingsCashRegister();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
  });

  useEffect(() => {
    if (isSuccess && cashRegister) {
      reset({
        name: cashRegister.name || '',
      });
    }
  }, [isSuccess, cashRegister]);

  const onSubmit = async (data: { name: string }) => {
    try {
      await updateCashRegister(cashRegister.uuid, data);
      setNotification(t('settings:sections.cashRegister.edit.messages.successUpdate'), { variant: 'success' });
      await refetchSettingsCashRegister();
      history.replace('/settings/cash');
      refetchCashRegister();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.response?.data?.errors?.[0].code === errorsCodeTypes.MONEY_CASH_REGISTER_NAME_EXISTING) {
        setError('name', {
          type: 'backend',
          message: error?.response?.data?.errors?.[0].message,
        });
        return;
      }
      setNotification(error.message || t('settings:sections.cashRegister.edit.messages.errorUpdate'), {
        variant: 'error',
      });
      history.replace('/settings/cash');
    }
  };

  return (
    <Layout>
      <ViewPanelLayout
        actionComponent={
          <ButtonPrimary
            disabled={isLoading || isSubmitting || !isValid || !isDirty}
            form="cash-register-form"
            type="submit"
          >
            {t('common:buttons.save')}
          </ButtonPrimary>
        }
        labelGoBack={t('common:buttons.toReturn')}
        onGoBack={() => history.replace('/settings/cash')}
      >
        <SettingsHeader px={0} title={t('settings:sections.cashRegister.edit.title')} />
        <Grid container direction="column" lg={5} sm={8} xs={12}>
          {isLoading ? (
            <>
              <Grid item xs={12}>
                <Skeleton height={14} variant="text" width={150} />
                <Skeleton height={42} variant="rectangular" />
              </Grid>
            </>
          ) : (
            <form id="cash-register-form" onSubmit={handleSubmit(onSubmit)}>
              <Grid item xs={12}>
                <InputTextFiled
                  autoFocus
                  disabled={isSubmitting}
                  error={Boolean(errors?.name)}
                  errorMsg={errors?.name?.message}
                  fullWidth
                  inputRef={register({
                    required: t('settings:sections.cashRegister.edit.form.inputName.required'),
                    minLength: {
                      value: 3,
                      message: t('settings:sections.cashRegister.edit.form.inputName.minLength'),
                    },
                    maxLength: {
                      value: 20,
                      message: t('settings:sections.cashRegister.edit.form.inputName.maxLength'),
                    },
                  })}
                  label={t('settings:sections.cashRegister.edit.form.inputName.label')}
                  name="name"
                  placeholder={t('settings:sections.cashRegister.edit.form.inputName.placeholder')}
                  required
                  title={errors?.name?.message}
                />
              </Grid>
            </form>
          )}
        </Grid>
      </ViewPanelLayout>
    </Layout>
  );
}

export default SettingsCashRegister;
